import React, { useEffect, useState } from 'react';

import ProductAnalyticsContextV100 from '@vimeo/bigpicture-client/types/events/product_analytics_context/100';
import WebContextV38 from '@vimeo/bigpicture-client/types/events/web_context/38';
import { User } from '@vimeo/gctl-api/models/components/schemas/user/User';
import {
  buildActionBpContext,
  buildProductAnalyticsBpContext,
  buildTeamBpContext,
  buildVideoBpContext,
  buildViewBpContext,
  buildWebBpContext,
  sendBpEventWithContexts,
} from '@vimeo/use-tracking';

function getViewContext() {
  return buildViewBpContext({
    view_type: 'impression',
    feature: null,
  });
}

function getActionContext() {
  return buildActionBpContext({
    action_type: 'click',
    feature: null,
  });
}

function getVideoContext(videoId: number, ownerId: number, videoPrivacy: string) {
  return buildVideoBpContext({
    video_id: videoId,
    video_owner_id: ownerId,
    video_privacy: videoPrivacy,
  });
}

function getVideoViewContext(videoId: number, sessionId: number) {
  return buildVideoBpContext({
    video_id: videoId,
    viewer_home_session_id: sessionId,
  });
}

type ReferrerUrl = string | null;
function getWebContext(
  pageName: WebContextV38['page_name'],
  referrerPage: WebContextV38['referrer_page_name'] = null,
  referrer: ReferrerUrl = null,
) {
  return buildWebBpContext({
    page_name: pageName,
    referrer_page_name: referrerPage,
    path: typeof window === 'undefined' ? '' : window.location.pathname,
    referrer,
  });
}

function getTeamContext(isTeamMember: boolean, teamOwnerId: number | null) {
  return buildTeamBpContext({
    is_team_member: isTeamMember,
    team_owner_id: teamOwnerId,
  });
}

function getProductAnalyticsContext(
  product: ProductAnalyticsContextV100['product'],
  feature: ProductAnalyticsContextV100['feature'],
  location: ProductAnalyticsContextV100['location'],
) {
  return buildProductAnalyticsBpContext({
    product,
    feature: feature,
    location: location,
    modal_name: null,
    flow: null,
    element: null,
    copy: 'sp',
    device_type: null,
    entity_type: 'user',
  });
}

const getTrackingDefaults = (): Pick<
  SpBadgeTracking,
  'pageName' | 'videoPrivacy' | 'isTeamMember' | 'teamOwnerId'
> => ({
  pageName: 'viewer_home',
  videoPrivacy: 'public',
  isTeamMember: false,
  teamOwnerId: null,
});

export function trackStaffPickBadgeEvent(
  spec: Pick<SpBadgeTracking, 'event' | 'videoId' | 'sessionId' | 'feature' | 'location'> & {
    userUri: string;
  },
) {
  const eventName = spec.event === 'view' ? 'vimeo.view_sp_badge' : 'vimeo.select_sp_badge';
  const eventTypeContext = spec.event === 'click' ? getActionContext() : getViewContext();

  const specWithDefaults = { ...getTrackingDefaults(), ...spec };

  const {
    videoId,
    userUri,
    feature,
    location,
    videoPrivacy,
    sessionId,
    pageName,
    isTeamMember,
    teamOwnerId,
  } = specWithDefaults;
  const ownerId = parseInt(userUri?.split('/')[2] ?? 0, 10);

  sendBpEventWithContexts(
    eventName,
    {
      ...eventTypeContext,
      ...getVideoContext(videoId, ownerId, videoPrivacy),
      ...getVideoViewContext(videoId, sessionId),
      ...getWebContext(pageName),
      ...getTeamContext(isTeamMember, teamOwnerId),
      ...getProductAnalyticsContext('viewer_home', feature, location),
    },
    1,
  );
}

export interface SpBadgeTracking {
  event: 'click' | 'view';
  videoId: number;
  ownerId: number;
  sessionId: number;
  feature: 'profile' | 'comments';
  location: 'video_details_panel' | 'comments_panel';
  pageName: WebContextV38['page_name'];
  videoPrivacy: 'public';
  isTeamMember: boolean;
  teamOwnerId: number | null;
}

export function useGetBpSessionId(): number {
  const [bpId, setBpId] = useState('');
  useEffect(() => {
    const bpMetaTag = document.querySelector('meta[name="bp-server-session-id"]');
    if (bpMetaTag) {
      setBpId(bpMetaTag.getAttribute('content') ?? '');
    } else {
      setBpId('');
    }
  }, []);

  return parseInt(bpId, 10);
}
